import SCENES from '../config/arcs';
import createMatrix from './matrix';
import debounce from '../utils/debounce';

const NS = 'http://www.w3.org/2000/svg';
const REF_W = 1440;
const REF_H = 1024;
const SLOTS = 5;

const INTRO_KEY = 'liralog.introSeen';
const INTRO_HOLD = 2600;
const INTRO_SKIP_PARAMS = /[?&](error|success|login|until)=/;

const clamp01 = t => Math.min(1, Math.max(0, t));
const lerp = (a, b, t) => a + (b - a) * t;
const easeInOut = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = t => 1 - Math.pow(1 - t, 3);

const prefersReducedMotion = () =>
  !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const hasFinePointer = () =>
  !!window.matchMedia && window.matchMedia('(pointer: fine)').matches;

const toRgb = hex => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const rgb = c => `rgb(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])})`;

const svgEl = (name, attrs = {}) => {
  const el = document.createElementNS(NS, name);
  Object.keys(attrs).forEach(key => el.setAttribute(key, attrs[key]));
  return el;
};

const spreadFor = W => Math.min(1.22, Math.max(1, 1 + (720 - W) / 1500));

const mixArc = (a, b, t) => ({
  d: a.d.map((v, i) => lerp(v, b.d[i], t)),
  w: lerp(a.w, b.w, t),
  c: a.c.map((stop, i) => stop.map((v, k) => lerp(v, b.c[i][k], t))),
});

const storage = {
  get(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  },
  set(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* private mode */ }
  },
};

/**
 * Builds the neon arcs inside `svg` and returns a small controller to place or morph them.
 */
function createArcs(svg, stage) {
  const defs = svgEl('defs');
  const clipRect = svgEl('rect', { x: 0, y: 0, width: 0, height: 0 });
  const clip = svgEl('clipPath', { id: 'stage-arcs-clip' });
  const group = svgEl('g', { 'clip-path': 'url(#stage-arcs-clip)' });

  clip.appendChild(clipRect);
  defs.appendChild(clip);
  svg.appendChild(defs);
  svg.appendChild(group);

  const slots = [];
  for (let i = 0; i < SLOTS; i++) {
    const gradient = svgEl('linearGradient', { id: `stage-arc-${i}`, gradientUnits: 'userSpaceOnUse' });
    const stops = [0, 0.5, 1].map(offset => gradient.appendChild(svgEl('stop', { offset })));
    const path = svgEl('path', { fill: 'none', stroke: `url(#stage-arc-${i})`, pathLength: 1 });

    defs.appendChild(gradient);
    group.appendChild(path);
    slots.push({ gradient, stops, path });
  }

  let state = null;
  let reveal = [];
  let clipAmount = 0;
  let scene = null;
  let raf = 0;
  let animating = false;
  let m = null;

  const measure = () => {
    const header = stage.querySelector('.site-header');
    const footer = stage.querySelector('.site-footer');
    const W = window.innerWidth;
    const H = window.innerHeight;

    return {
      W,
      H,
      top: header ? header.getBoundingClientRect().bottom : 0,
      footerTop: footer ? footer.getBoundingClientRect().top : H,
    };
  };

  const resolve = name => {
    const { arcs, clip: clipMode } = SCENES[name];
    const { W, H } = m;

    return {
      clip: clipMode === 'footer' ? 1 : 0,
      arcs: arcs.map(arc => {
        const d = arc.d.slice();
        const card = arc.anchor === 'card' && stage.querySelector('.card');

        // Drop the beam from the header rule straight into the top of the card.
        if (card) {
          const rect = card.getBoundingClientRect();
          const x = REF_W / 2 + (rect.left + rect.width / 2 - W / 2) / (W / REF_W * spreadFor(W));
          const end = (rect.top + Math.min(90, rect.height / 3)) / H * REF_H;
          d[0] = d[2] = d[4] = d[6] = x;
          d[3] = lerp(d[1], end, 1 / 3);
          d[5] = lerp(d[1], end, 2 / 3);
          d[7] = end;
        }

        return { d, w: arc.w, c: arc.c.map(toRgb) };
      }),
    };
  };

  const paint = () => {
    if (!state) return;

    const { W, H, top, footerTop } = m;
    const sy = H / REF_H;
    // On narrow screens push the arcs outwards so they frame the content instead of crossing it.
    const sx = W / REF_W * spreadFor(W);
    const sw = Math.min(1.25, Math.max(0.5, Math.min(W / REF_W, sy)));
    const bottom = lerp(H, Math.min(H, footerTop), clipAmount);
    const px = x => (W / 2 + (x - REF_W / 2) * sx).toFixed(1);
    const py = y => (y * sy).toFixed(1);
    const point = (x, y) => `${px(x)} ${py(y)}`;

    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    clipRect.setAttribute('y', top);
    clipRect.setAttribute('width', W);
    clipRect.setAttribute('height', Math.max(0, bottom - top));

    state.forEach((arc, i) => {
      const { path, gradient, stops } = slots[i];
      const [x0, y0, c1x, c1y, c2x, c2y, x1, y1] = arc.d;

      path.setAttribute('d', `M${point(x0, y0)}C${point(c1x, c1y)} ${point(c2x, c2y)} ${point(x1, y1)}`);
      path.setAttribute('stroke-width', (arc.w * sw).toFixed(1));

      gradient.setAttribute('x1', px(x0));
      gradient.setAttribute('y1', py(y0));
      gradient.setAttribute('x2', px(x1));
      gradient.setAttribute('y2', py(y1));
      stops.forEach((stop, k) => stop.setAttribute('stop-color', rgb(arc.c[k])));

      if (reveal[i] < 1) {
        path.setAttribute('stroke-dasharray', '1 1');
        path.setAttribute('stroke-dashoffset', (1 - reveal[i]).toFixed(4));
      } else {
        path.removeAttribute('stroke-dasharray');
        path.removeAttribute('stroke-dashoffset');
      }
    });
  };

  const run = (duration, frame) => new Promise(done => {
    cancelAnimationFrame(raf);
    animating = true;
    const start = performance.now();

    const step = now => {
      const elapsed = now - start;
      frame(elapsed);
      paint();
      if (elapsed < duration) raf = requestAnimationFrame(step);
      else {
        animating = false;
        done();
      }
    };

    raf = requestAnimationFrame(step);
  });

  return {
    /** Places the arcs of `name` immediately. `hidden` leaves them undrawn for `drawIn`. */
    set(name, { hidden = false } = {}) {
      cancelAnimationFrame(raf);
      animating = false;
      m = measure();
      const target = resolve(name);
      state = target.arcs;
      clipAmount = target.clip;
      reveal = state.map(() => (hidden ? 0 : 1));
      scene = name;
      paint();
    },

    /** Draws the current arcs along their length, one after another. */
    drawIn({ duration = 1200, stagger = 140 } = {}) {
      return run(duration + stagger * (SLOTS - 1), elapsed => {
        reveal = reveal.map((_, i) => easeOut(clamp01((elapsed - i * stagger) / duration)));
      });
    },

    /** Morphs every arc (shape, width and colors) into the layout of `name`. */
    morph(name, { duration = 1500, stagger = 80 } = {}) {
      m = measure();
      const from = state;
      const fromClip = clipAmount;
      const target = resolve(name);
      const total = duration + stagger * (SLOTS - 1);
      reveal = reveal.map(() => 1);

      return run(total, elapsed => {
        state = from.map((arc, i) =>
          mixArc(arc, target.arcs[i], easeInOut(clamp01((elapsed - i * stagger) / duration))));
        clipAmount = lerp(fromClip, target.clip, easeInOut(clamp01(elapsed / total)));
      }).then(() => { scene = name; });
    },

    resize() {
      m = measure();
      if (!animating && scene) {
        const target = resolve(scene);
        state = target.arcs;
        clipAmount = target.clip;
      }
      paint();
    },
  };
}

const markReady = (stage, arriving = true) => {
  stage.classList.add('is-ready');
  if (arriving) stage.classList.add('is-arriving');
};

const textBox = el => {
  const range = document.createRange();
  range.selectNodeContents(el);
  return range.getBoundingClientRect();
};

const focusFirstField = (stage, force = false) => {
  const input = stage.querySelector('.card .field__input');
  if (input && (force || hasFinePointer())) input.focus({ preventScroll: true });
};

/**
 * Splash → login: the splash title flies into the card while the arcs regroup around it.
 */
function enterLogin(stage, arcs, { focus = false } = {}) {
  const intro = stage.querySelector('.intro');
  const hero = stage.querySelector('.intro__title');
  const title = stage.querySelector('.card__title');
  let flight = null;

  // Measure while the card still sits hidden in its resting place, before its
  // entrance animation shifts it. The card title is a full-width block, so the
  // landing size comes from its text, not from the block.
  if (hero && title) {
    hero.style.animation = 'none';
    const from = hero.getBoundingClientRect();
    const to = title.getBoundingClientRect();
    const text = textBox(title);
    flight = `translate(${text.left - from.left}px, ${to.top - from.top}px) scale(${text.width / from.width})`;
  }

  stage.dataset.scene = 'login';
  stage.classList.add('is-morphing');
  if (focus) focusFirstField(stage, true);

  if (flight) {
    requestAnimationFrame(() => {
      hero.style.transition = 'transform 1100ms cubic-bezier(.7, 0, .2, 1)';
      hero.style.transform = flight;
    });
  }

  arcs.morph('login', { duration: 1500, stagger: 70 });

  setTimeout(() => stage.classList.add('is-landed'), 1150);
  setTimeout(() => {
    stage.classList.remove('is-morphing', 'is-landed');
    if (intro) intro.hidden = true;
    if (!focus) focusFirstField(stage);
  }, 2000);
}

function runIntro(stage, arcs, { reduce, viaTransition }) {
  const skip = reduce || viaTransition
    || INTRO_SKIP_PARAMS.test(window.location.search)
    || storage.get(INTRO_KEY);

  if (skip) {
    stage.dataset.scene = 'login';
    arcs.set('login');
    markReady(stage, !viaTransition);
    focusFirstField(stage);
    return;
  }

  arcs.set('intro', { hidden: true });
  markReady(stage, false);
  arcs.drawIn();

  const events = ['pointerdown', 'keydown', 'wheel'];
  let timer = null;

  const leave = event => {
    clearTimeout(timer);
    events.forEach(type => window.removeEventListener(type, leave));
    storage.set(INTRO_KEY, '1');

    const fromKeyboard = !!event && event.type === 'keydown';
    if (fromKeyboard && event.key === 'Tab') event.preventDefault();

    enterLogin(stage, arcs, { focus: fromKeyboard });
  };

  timer = setTimeout(leave, INTRO_HOLD);
  events.forEach(type => window.addEventListener(type, leave));
}

/**
 * True when the browser is probably animating this page in with a cross-document
 * view transition, so our own entrance should stay out of the way.
 */
function arrivedViaViewTransition() {
  if (!('onpagereveal' in window) || !document.referrer) return false;
  try {
    return new URL(document.referrer).origin === window.location.origin;
  } catch (e) {
    return false;
  }
}

/**
 * Starts the background (binary map + neon arcs) and plays the scene set on `.stage[data-scene]`.
 */
export default function initStage() {
  const stage = document.querySelector('.stage');
  if (!stage) return null;

  const reduce = prefersReducedMotion();
  const viaTransition = arrivedViaViewTransition();
  if (viaTransition) stage.classList.add('via-transition');

  const matrix = createMatrix(stage.querySelector('.stage__matrix'), { animate: !reduce });

  const arcs = createArcs(stage.querySelector('.stage__arcs'), stage);

  Array.prototype.forEach.call(stage.querySelectorAll('.card__form > *'), (el, i) => {
    el.style.setProperty('--i', i);
  });

  let lastWidth = window.innerWidth;
  let lastHeight = window.innerHeight;
  window.addEventListener('resize', debounce(() => {
    const widthChanged = window.innerWidth !== lastWidth;
    const heightChanged = Math.abs(window.innerHeight - lastHeight) > lastHeight * 0.15;
    lastWidth = window.innerWidth;
    lastHeight = window.innerHeight;
    // Mobile toolbars nudge the height while scrolling; only redraw digits on real resizes.
    if (widthChanged || heightChanged) matrix.resize();
    arcs.resize();
  }, 150));

  const scene = stage.dataset.scene || 'login';

  if (scene === 'intro') {
    runIntro(stage, arcs, { reduce, viaTransition });
  } else {
    arcs.set(scene);
    markReady(stage, !viaTransition);
  }

  return { stage, arcs };
}
